import React, { CSSProperties, useMemo, useState } from 'react';
import { Tag, UserRoleEnum, useGetClientsQuery, useGetProjectsSummaryQuery } from '../../generated/graphql';
import { FilterTag } from '../../App.enums';
import NewClientDialog from '../../components/dialogs/NewClientDialog/NewClientDialog';
import DeleteClientsDialog from '../../components/dialogs/DeleteClientsDialog/DeleteClientsDialog';
import AddClientsTagsDialog from '../../components/dialogs/AddClientsTagsDialog/AddClientsTagsDialog';
import AddClientHealthChecksDialog from '../../components/dialogs/AddClientHealthChecksDialog/AddClientHealthChecksDialog';
import ShowOnRole from '../../components/main/ShowOnRole/ShowOnRole';
import SearchBoxNew from '../../components/main/SearchBoxNew/SearchBoxNew';
import SpinnerNew from '../../components/main/SpinnerNew/SpinnerNew';
import { Link } from 'react-router-dom';
import { Plus, Tags, Activity, Trash2, Users, Building2, LayoutGrid, List, PackageOpen, EyeOff, Eye } from 'lucide-react';
import RefreshButton from '../../components/main/RefreshButton/RefreshButton';
import clsx from 'clsx';
import TagBadge from '../../components/main/TagBadge/TagBadge';

type ClientsProps = {
    style?: CSSProperties | undefined;
};

const Clients = (props: ClientsProps) => {
    const { style } = props;
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [excludeTagIds, setExcludeTagIds] = useState<string[]>([]);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [showPrivate, setShowPrivate] = useState(false);
    const [newClientDialogVisible, setNewClientDialogVisible] = useState(false);
    const [deleteClientsDialogVisible, setDeleteClientsDialogVisible] = useState(false);
    const [assignTagsDialogVisible, setAssignTagsDialogVisible] = useState(false);
    const [assignHealthCheckVisible, setAssignHealthCheckVisible] = useState(false);

    const result = useGetClientsQuery({
        variables: {
            tags: tagIds,
            contains: searchText
        },
    });

    // Discover private clients the user has access to via their projects
    const projectsSummaryResult = useGetProjectsSummaryQuery();

    const clients = result.data?.ClientList?.nodes || [];

    // Merge private clients from projects that aren't already in the clients list
    const allClients = useMemo(() => {
        const existingIds = new Set(clients.map((c: any) => c.id));
        const nodes = projectsSummaryResult.data?.ProjectList?.nodes;
        if (!nodes) return clients;

        const extraClients = new Map<string, any>();
        for (const project of nodes) {
            const client = project?.client;
            if (!client?.id || !client?.isPrivate || existingIds.has(client.id) || extraClients.has(client.id)) continue;
            if (searchText && !client.name?.toLowerCase().includes(searchText.toLowerCase())) continue;
            extraClients.set(client.id, {
                id: client.id,
                name: client.name,
                isPrivate: true,
                isAdmin: client.isAdmin,
                longName: null,
                tags: [],
                healthChecks: [],
                admins: [],
                tagIds: [],
            });
        }

        return extraClients.size > 0 ? [...clients, ...Array.from(extraClients.values())] : clients;
    }, [clients, projectsSummaryResult.data, searchText]);

    const hasPrivateClients = allClients.some((c: any) => c.isPrivate);
    const baseClients = showPrivate ? allClients : allClients.filter((c: any) => !c.isPrivate);
    const visibleClients = excludeTagIds.length > 0
        ? baseClients.filter((c: any) => !excludeTagIds.some(id => (c.tagIds as string[] | undefined)?.includes(id)))
        : baseClients;

    const isAdminOfSelected = selectedIds.length > 0 && selectedIds.some(
        id => allClients.find((c: any) => c.id === id)?.isAdmin
    );

    const handleOnClose = () => {
        setNewClientDialogVisible(false);
        setDeleteClientsDialogVisible(false);
        setAssignTagsDialogVisible(false);
        setAssignHealthCheckVisible(false);
        setSelectedIds([]);
        result.refetch();
    };

    const handleFilterChanged = (tags: FilterTag[]) => {
        setTagIds(tags.filter(t => !t.inverse).map(t => t.id || ""));
        setExcludeTagIds(tags.filter(t => t.inverse).map(t => t.id || ""));
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    return (
        <>
            <div className="page-container" style={style} data-testid="Clients">
                {/* Header */}
                <div className="page-header">
                    <div>
                        <h1 className="page-title">Clients</h1>
                        <p className="page-subtitle">
                            Manage your client organizations and their content
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <RefreshButton onRefresh={() => result.refetch()} className="btn-ghost" iconSize={16} showLabel={false} />
                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdminOfSelected || false}>
                            {selectedIds.length > 0 && (
                                <>
                                    <button className="btn-secondary" onClick={() => setAssignTagsDialogVisible(true)}>
                                        <Tags size={16} />
                                        Tags
                                    </button>
                                    <button className="btn-secondary" onClick={() => setAssignHealthCheckVisible(true)}>
                                        <Activity size={16} />
                                        Health Checks
                                    </button>
                                </>
                            )}
                        </ShowOnRole>
                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={false}>
                            {selectedIds.length > 0 && (
                                <button className="btn-danger" onClick={() => setDeleteClientsDialogVisible(true)}>
                                    <Trash2 size={16} />
                                    Delete ({selectedIds.length})
                                </button>
                            )}
                            <button className="btn-primary" onClick={() => setNewClientDialogVisible(true)}>
                                <Plus size={16} />
                                New Client
                            </button>
                        </ShowOnRole>
                    </div>
                </div>

                {/* Search & View Toggle */}
                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <SearchBoxNew
                            onSearchChanged={setSearchText}
                            onFilterChanged={handleFilterChanged}
                        />
                    </div>
                    {hasPrivateClients && (
                        <button
                            className={clsx('flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors')}
                            style={{
                                backgroundColor: showPrivate ? 'var(--accent-soft)' : 'var(--surface-100)',
                                color: showPrivate ? 'var(--accent)' : 'var(--text-tertiary)',
                                border: 'none', cursor: 'pointer', outline: 'none',
                            }}
                            onClick={() => setShowPrivate(p => !p)}
                            title={showPrivate ? 'Hide private clients' : 'Show private clients'}
                        >
                            {showPrivate ? <Eye size={15} /> : <EyeOff size={15} />}
                            Private
                        </button>
                    )}
                    <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--surface-100)' }}>
                        <button
                            className={clsx('p-2 rounded-md transition-all', viewMode === 'grid' ? '' : '')}
                            style={{
                                backgroundColor: viewMode === 'grid' ? 'var(--bg-card)' : 'transparent',
                                boxShadow: viewMode === 'grid' ? 'var(--shadow-sm)' : 'none',
                                color: viewMode === 'grid' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                                border: 'none', cursor: 'pointer', outline: 'none',
                            }}
                            onClick={() => setViewMode('grid')}
                        >
                            <LayoutGrid size={16} />
                        </button>
                        <button
                            className="p-2 rounded-md transition-all"
                            style={{
                                backgroundColor: viewMode === 'list' ? 'var(--bg-card)' : 'transparent',
                                boxShadow: viewMode === 'list' ? 'var(--shadow-sm)' : 'none',
                                color: viewMode === 'list' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                                border: 'none', cursor: 'pointer', outline: 'none',
                            }}
                            onClick={() => setViewMode('list')}
                        >
                            <List size={16} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-h-0 overflow-auto">
                {result.loading ? (
                    <SpinnerNew size="large" label="Loading clients..." />
                ) : visibleClients.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <PackageOpen size={24} />
                        </div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No clients found</p>
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Try adjusting your search or create a new client</p>
                    </div>
                ) : viewMode === 'grid' ? (
                    /* Card Grid View */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in">
                        {visibleClients.map((client: any) => (
                            <div
                                key={client.id}
                                className={clsx('card p-5 group relative')}
                                style={{
                                    borderColor: selectedIds.includes(client.id) ? 'var(--accent)' : undefined,
                                    backgroundColor: selectedIds.includes(client.id) ? 'var(--accent-soft)' : undefined,
                                }}
                            >
                                {/* Selection checkbox */}
                                <div className="absolute top-4 right-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(client.id)}
                                        onChange={() => toggleSelect(client.id)}
                                        className="w-4 h-4 rounded cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{
                                            accentColor: 'var(--accent)',
                                            opacity: selectedIds.includes(client.id) ? 1 : undefined,
                                        }}
                                    />
                                </div>

                                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                                    style={{ backgroundColor: 'var(--accent-soft)' }}>
                                    <Building2 size={20} style={{ color: 'var(--accent)' }} />
                                </div>

                                <Link
                                    to={`/clients/${client.id}`}
                                    className="text-sm font-semibold hover:underline block mb-1"
                                    style={{ color: 'var(--text-primary)' }}
                                >
                                    {client.name}
                                </Link>

                                {client.longName && (
                                    <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                                        {client.longName}
                                    </p>
                                )}

                                {/* Tags */}
                                {client.tags && client.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-auto pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                        {client.tags.filter((t: any) => t).slice(0, 3).map((t: Tag) => (
                                            <TagBadge key={t.id} tag={t} showType={false} className="text-xxs" />
                                        ))}
                                        {client.tags.filter((t: any) => t).length > 3 && (
                                            <span className="badge-default text-xxs">
                                                +{client.tags.filter((t: any) => t).length - 3}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Health Checks count */}
                                {client.healthChecks && client.healthChecks.length > 0 && (
                                    <div className="flex items-center gap-1.5 mt-2">
                                        <Activity size={12} style={{ color: 'var(--success)' }} />
                                        <span className="text-xxs" style={{ color: 'var(--text-tertiary)' }}>
                                            {client.healthChecks.length} health check{client.healthChecks.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                )}

                                {/* Client Admins */}
                                {client.admins && client.admins.filter((a: any) => a).length > 0 && (
                                    <div className="flex items-center gap-1.5 mt-2">
                                        <Users size={12} style={{ color: 'var(--accent)' }} />
                                        <span className="text-xxs" style={{ color: 'var(--text-tertiary)' }}>
                                            {client.admins.filter((a: any) => a).map((a: any) => a.name).join(', ')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    /* List View */
                    <div className="card overflow-auto animate-fade-in" style={{ maxHeight: '100%' }}>
                        <table className="w-full border-collapse">
                            <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--bg-card)' }}>
                                <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                    <th className="w-12 px-4 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            checked={visibleClients.length > 0 && selectedIds.length === visibleClients.length}
                                            onChange={() => {
                                                if (selectedIds.length === visibleClients.length) setSelectedIds([]);
                                                else setSelectedIds(visibleClients.map((c: any) => c.id));
                                            }}
                                            className="w-4 h-4 rounded cursor-pointer"
                                            style={{ accentColor: 'var(--accent)' }}
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Long Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Admins</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Health Checks</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Tags</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleClients.map((client: any) => (
                                    <tr
                                        key={client.id}
                                        className="transition-colors duration-100"
                                        style={{
                                            borderBottom: '1px solid var(--border-subtle)',
                                            backgroundColor: selectedIds.includes(client.id) ? 'var(--accent-soft)' : 'transparent',
                                        }}
                                        onMouseOver={(e) => {
                                            if (!selectedIds.includes(client.id)) e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = selectedIds.includes(client.id) ? 'var(--accent-soft)' : 'transparent';
                                        }}
                                    >
                                        <td className="w-12 px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(client.id)}
                                                onChange={() => toggleSelect(client.id)}
                                                className="w-4 h-4 rounded cursor-pointer"
                                                style={{ accentColor: 'var(--accent)' }}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <Link to={`/clients/${client.id}`} className="text-sm font-medium hover:underline" style={{ color: 'var(--accent-text)' }}>
                                                {client.name}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{client.longName}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {client.admins?.filter((a: any) => a).map((a: any) => (
                                                    <span key={a.id} className="badge-accent">{a.name}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{client.healthChecks?.length || 0}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {client.tags?.filter((t: any) => t).map((t: Tag) => (
                                                    <TagBadge key={t.id} tag={t} showType={false} />
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>
            </div>
            {newClientDialogVisible && <NewClientDialog visible={newClientDialogVisible} onClose={handleOnClose} />}
            {deleteClientsDialogVisible && <DeleteClientsDialog ids={selectedIds} visible={deleteClientsDialogVisible} onClose={handleOnClose} />}
            {assignTagsDialogVisible && <AddClientsTagsDialog ids={selectedIds} visible={assignTagsDialogVisible} onClose={handleOnClose} />}
            {assignHealthCheckVisible && <AddClientHealthChecksDialog ids={selectedIds} visible={assignHealthCheckVisible} onClose={handleOnClose} />}
        </>
    );
};

export default Clients;
